import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { Button, Item, Form } from "semantic-ui-react";
import DropDown from "../../components/shared/dropdown";
import { RateDiscountTypes, RateDiscountTypesEnum, DiscountTypesEnum, DiscountPropertiesEnum, DiscountApplicationTypes, ERateCategory, maxNumericInputValue, dateFormat } from "../../components/shared/constants";
import { useMessageResources, useTextResources } from "../../language";
import { getEntityDateTimeRange, makeIntToDaysOfWeekArray, formatValueAsCurrency, getWeekDaysOfRange, isCovered} from '../../components/shared/utils'
import { onError } from '../../components/shared/messages';
import { DatePicker } from "../../components/shared/DatePicker";
import { Rates } from "../../api/services/next";
import { PriceListContext } from "./context";
import { DiscountPopup } from "./Adjustments/DiscountPopup"
import { useConfirm } from "../../hooks/useConfirm";
import { useDateTimeRange } from "../../hooks/useDateTimeRange";
import CheckboxWrapper from "../../components/shared/Checkbox";
import { SyncRateToChannelManager } from "../../api/services/pms";
import Weekdays from "../../components/shared/Weekdays";
import moment from "moment";
import { TimelineMinimap } from "../../components/TimelineMinimap";
import { useLoader } from "../../hooks/useLoader";

export const Adjustments = () => {
    const text = useTextResources();
    const message = useMessageResources();
    const loader = useLoader();
    const confirm  = useConfirm();

    const { state, setState } = useContext(PriceListContext);
	const [ showInactive, setShowInactive ] = useState(false);
    const allRateDiscounts = useMemo(() => state.RateDiscounts ?? [], [state.RateDiscounts]);
    const [selectedDiscountType, setSelectedDiscountType] = useState(0);
    const [discountPopupProps, setDiscountPopupProps] = useState({
        open: false,
        discountToEdit: undefined,
        rateDiscountTypeToCreate: undefined,
    });
    const currencyId = state.CurrencyId;

    const {
        startDate: filterFromDate,
        endDate: filterToDate,
        setStartDate: setFilterFromDate,
        setEndDate: setFilterToDate,
    } = useDateTimeRange({
        clearable: true,
        startDate: undefined,
        endDate: undefined
    });

    const loadRateDiscounts = useCallback(async () => {
        try {
            loader(true);
            const rateDiscounts = await Rates.GetRateDiscounts({
                RateId: state.Id,
                IncludeParentRateDiscounts: true,
                MultiRateDiscountsOnly: false,
				InActive: true,
            });
            setState((prev) => ({ ...prev, RateDiscounts: rateDiscounts }));

        } catch (error) {
            onError(error);
        }
        finally {
            loader(false);
        }
    }, [loader, setState, state.Id]);

    useEffect(() => {
        loadRateDiscounts();
    }, [loadRateDiscounts]);

    const deactivateRateDiscount = useCallback(async (id) => {
        try {
            loader(true);
            await Rates.DeactivateRateDiscounts([id]);
            await SyncRateToChannelManager({
                      PMSHotelID: state.HotelId,
                      ExternalPMSRateID: state.Id,
                      eRateCategory: ERateCategory.FullRate
                    });

            await loadRateDiscounts();
        } catch (error) {
            onError(error);
        }
        finally {
            loader(false);
        }
    }, [loader, loadRateDiscounts]);

    const openCreateDiscountPopup = useCallback((rateDiscountTypeToCreate, savedDiscount) => {
        setSelectedDiscountType(rateDiscountTypeToCreate);
        if (rateDiscountTypeToCreate > 0) {
            const discountForEdit = savedDiscount ? savedDiscount : {
                RateId: state.Id,
                RateDiscountAmounts: [],
                ApplicableNight: rateDiscountTypeToCreate == RateDiscountTypesEnum.DiscountForEveryXNight ? 1 : null,
                RepeatDailyDiscount: false,
                RateDiscountTypeId: rateDiscountTypeToCreate,
                DiscountApplicationTypeId: 1,
            };
            setDiscountPopupProps({
                open: true,
                rateDiscountTypeToCreate,
                discountForEdit: discountForEdit,
            });
        }
    }, [state.Id]);

    const openEditDiscountPopup = useCallback((discountForEdit) => {
        setDiscountPopupProps({
            open: true,
            rateDiscountTypeToCreate: discountForEdit.RateDiscountTypeId,
            discountForEdit,
        });
    }, []);

    const handleEditDiscount = useCallback((discount) => {
        openEditDiscountPopup(discount)
    }, [openEditDiscountPopup]);

    const handleDeactivateDiscount = useCallback(async (id) => {
        await confirm({
            header: message('AreYouSure'),
            content: message('AreYouSureYouWouldLikeToDeleteSelectedItems'),
            confirmButton: {
                content: text('Yes'),
                positive: true,
            },
            cancelButton: {
                content: text('No'),
                negative: true,
            },
            onConfirm: () => {
                deactivateRateDiscount(id);
            },
        });
    }, [confirm, message, text, deactivateRateDiscount]);

    const closeDiscountPopup = useCallback(() => {
        setSelectedDiscountType(0);
        setDiscountPopupProps({
            open: false,
            discountForEdit: undefined,
            rateDiscountType: undefined,
        });
    }, []);

    const handleSave = useCallback(async (savedDiscount) => {
        await loadRateDiscounts();
        if (savedDiscount.Id) {
            openEditDiscountPopup(savedDiscount);
        } else {
            openCreateDiscountPopup(selectedDiscountType, savedDiscount);
        }
    }, [loadRateDiscounts, openCreateDiscountPopup, openEditDiscountPopup, selectedDiscountType])

    const isDiscountTypeDisabledBasedOnOtherRateDiscounts = useCallback((discountType) => {
        const otherDiscountTypeStr = discountType === DiscountTypesEnum.Amount
            ? DiscountPropertiesEnum.DiscountPercent
            : state?.IsProductPricingIncludesTax
                ? DiscountPropertiesEnum.DiscountAmountAfterTax
                : DiscountPropertiesEnum.DiscountAmountBeforeTax;

        const minValue = discountType === DiscountTypesEnum.Amount ? -maxNumericInputValue : -100
        return allRateDiscounts.some(rd => rd.RateDiscountAmounts?.some(rda => rda[otherDiscountTypeStr] && rda[otherDiscountTypeStr] >= minValue) && rd.IsActive);
    }, [state?.IsProductPricingIncludesTax, allRateDiscounts]);


    const isDiscountTypeEnabled = useCallback((discountType) => {
      const hasActiveDiscounts = allRateDiscounts.some((rd) => rd.IsActive);

      if (!hasActiveDiscounts) {
        // Enable all options if there are no active discounts
        return true;
      }

      // Check if there are active discounts of a different type
      if (discountType === RateDiscountTypesEnum.OverallDiscount) {
        // Disable OverallDiscount if there are active DiscountForEveryXNight type discounts
        return !allRateDiscounts.some(
          (rd) =>
            rd.RateDiscountTypeId ===
              RateDiscountTypesEnum.DiscountForEveryXNight && rd.IsActive
        );
      } else if (
        discountType === RateDiscountTypesEnum.DiscountForEveryXNight
      ) {
        // Disable DiscountForEveryXNight if there are active OverallDiscount type discounts
        return !allRateDiscounts.some(
          (rd) =>
            rd.RateDiscountTypeId === RateDiscountTypesEnum.OverallDiscount &&
            rd.IsActive
        );
      }

      return true;
    }, [allRateDiscounts]);

    const discountTypesDropDownData = useMemo(() => [
        { Id: 0, Name: text("AddNewDiscount") },
        { Id: 1, Name: text(RateDiscountTypes[RateDiscountTypesEnum.OverallDiscount]), disabled: !isDiscountTypeEnabled(RateDiscountTypesEnum.OverallDiscount) },
        { Id: 3, Name: text(RateDiscountTypes[RateDiscountTypesEnum.DiscountForEveryXNight]), disabled: !isDiscountTypeEnabled(RateDiscountTypesEnum.DiscountForEveryXNight) }
    ], [isDiscountTypeEnabled, text]);




    const rateDiscountsToDisplay = useMemo(() => {
        const items = allRateDiscounts.map((item) => ({
            discount: item,
            dateRange: getEntityDateTimeRange(item),
        }));

        const itemsInRange = items.filter((item) =>
            (!filterFromDate || filterFromDate.valueOf() < item.dateRange.end.valueOf()) &&
            (!filterToDate || filterToDate.valueOf() > item.dateRange.start.valueOf()) &&
			(showInactive || item.discount.IsActive));

        const allowedWeekDays = getWeekDaysOfRange(filterFromDate || new Date(), filterToDate || new Date());
        const visibleItems = itemsInRange.filter(item => !(filterFromDate && filterToDate) ||
                                                         makeIntToDaysOfWeekArray(item.discount.Weekdays).some(d => allowedWeekDays.includes(d)));

        visibleItems.sort((left, right) => {
            return left.dateRange.start.valueOf() - right.dateRange.start.valueOf();
        });

        return visibleItems;
    }, [allRateDiscounts, filterFromDate, filterToDate, showInactive]);

    return (
        <div className='adjustments'>
            <Form>
                <Form.Group widths={4}>
                    <DatePicker
                        label={`${text("FilterBy")} ${text("FromDate")}`}
                        value={filterFromDate}
                        onChange={setFilterFromDate}
                        clearable
                        minDate={moment(state.StartDate).startOf("day").toDate()}
                        maxDate={new Date(state.EndDate)}
                    />
                    <DatePicker
                        label={`${text("FilterBy")} ${text("ToDate")}`}
                        value={filterToDate}
                        onChange={setFilterToDate}
                        clearable
                        minDate={moment(state.StartDate).startOf("day").toDate()}
                        maxDate={new Date(state.EndDate)}
                    />
                    <CheckboxWrapper
						label={text("IncludeInactive")}
						value={showInactive}
						onChange={setShowInactive}
					/>
                    <Form.Field
                        label={text("AddNewDiscount")}
                        control={DropDown}
                        value={selectedDiscountType}
                        data={discountTypesDropDownData}
                        onChange={openCreateDiscountPopup}
                        compact={false}
                        placeholder={`${text("Select")}...`}
                        textField="Name"
                        valueField="Id"
                    />
                </Form.Group>
            </Form>
            <TimelineMinimap priceList={state} elements={allRateDiscounts.filter(el => el.IsActive)} />
            <Item.Group divided>
                {rateDiscountsToDisplay.length !== 0
                    ? rateDiscountsToDisplay.map((item) => { 
                        return (
                        <RateDiscountItem
                            key={item.discount.Id}
                            rateId={state.Id}
                            discount={item.discount}
                            state={state}
                            onEdit={handleEditDiscount}
                            onDelete={handleDeactivateDiscount}
                            currencyId={currencyId}
                        /> )
                    })
                    : text("NoItemsToDisplay")
                }
            </Item.Group>
            {discountPopupProps.open && <DiscountPopup
                {...discountPopupProps}
                onSaveComplete={handleSave}
                onClose={closeDiscountPopup}
                existingRateDiscounts={allRateDiscounts}
                isDiscountTypeDisabledBasedOnOtherRateDiscounts={isDiscountTypeDisabledBasedOnOtherRateDiscounts}
            />}
        </div>
    );
};

const RateDiscountItem = ({
    rateId,
    discount,
    state,
    onEdit,
    onDelete,
    currencyId
}) => {
    const text = useTextResources();


    const handleEdit = useCallback(() => {
        onEdit(discount);
    }, [discount, onEdit]);

    const handleDelete = useCallback(() => {
        onDelete(discount.Id);
    }, [discount.Id, onDelete]);

    const getDiscountDescription = (discount) => {
        var discountValue = getDiscountValueDescription(discount.RateDiscountAmounts, currencyId);
        var discountTitle = '';

        switch (discount.RateDiscountTypeId) {
            case RateDiscountTypesEnum.OverallDiscount:
                discountTitle = text("OverallDiscountDescription").replace("{0}", `${discountValue}`);
                break;
            case RateDiscountTypesEnum.DiscountForEveryXNight:
                discountTitle = text(discount.RepeatDailyDiscount ? "DiscountForEveryXNightDescription" : "DiscountForXNightDescription")
                    .replace("{0}", `${discountValue}`).replace("{1}", discount.ApplicableNight || 0);
                break;
            default:
                discountTitle = '';
                break;
        }
        return discountTitle;
    }

    const getDiscountSecondaryDescription = (discount) => {
        var discountTitle = '';

        var applicationType = DiscountApplicationTypes[discount.DiscountApplicationTypeId];
        var appliesOn = `${text("DiscountAppliesOn").replace("{0}", text(applicationType))}.`;

        discountTitle += appliesOn;

        if (discount.MinimumNumberOfNights || discount.MaximumNumberOfNights) {
            discountTitle += ` ${text("MinMaxOfNights").replace("{0}", discount.MinimumNumberOfNights || '_').replace("{1}", discount.MaximumNumberOfNights || '_')}`;
        }
        return discountTitle;
    }

    const getDiscountValueDescription = (amounts) => {
        if (!amounts?.length) return '_';

        const isByAmount = amounts.some(a => a.DiscountAmount != null);

        if (isByAmount) {
            const discountAmounts = amounts.filter(a => a.DiscountAmountBeforeTax != 0);

            const valuesBeforeTax = discountAmounts.map(a => a.DiscountAmountBeforeTax);
            const isSameBeforeTax = valuesBeforeTax.every(v => v === discountAmounts[0].DiscountAmountBeforeTax);

            if (isSameBeforeTax) {
                return `${text('AmountBeforeTax')}: ${formatValueAsCurrency(discountAmounts[0].DiscountAmountBeforeTax, currencyId)}, 
                        ${text('AmountAfterTax')}: ${formatValueAsCurrency(discountAmounts[0].DiscountAmountAfterTax, currencyId)}`;
            } else {
                return `${text('DifferentAmounts')}`;
            }
        } else {
            const valuesPercent = amounts.filter(a => a.DiscountPercent != 0).map(a => a.DiscountPercent);
            const isSamePercent = valuesPercent.every(v => v === valuesPercent[0]);
    
            return isSamePercent ? `${valuesPercent[0]}%` : `${text('DifferentPercentages')}`;
        }
    };

    const { startDate, endDate } = { startDate: discount.StartDate, endDate: discount.EndDate };

    const inherited = useMemo(() => discount.RateId !== rateId, [discount.RateId, rateId]);
    const elements = state.RateDiscounts;
    const coverage = isCovered(state, elements)
    const asDate = (date) => moment(date).format(dateFormat)
    const startStyle = ()=>{
        if (coverage.hasStartGap && coverage.earliestStart.isSame(startDate, 'day')) {
            return { color: "#F2711C" };
        }
        else {
            return null;
        }
    }
    const endStyle = ()=>{
        if (coverage.hasEndGap && coverage.latestEnd.isSame(endDate, 'day')) {
            return { color: "#F2711C" };
        }
        else {
            return null;
        }
    }
    return (
        <Item key={discount.Id} className={inherited && "inherited"} >
            <div className={discount.IsActive ? '' : 'disabled'}>
                <Item.Header as='a'>
                    {text(RateDiscountTypes[discount.RateDiscountTypeId])} {inherited && `(${text("Inherited")})`}
                </Item.Header>
                <Item.Meta>
                    <div>
                    <span style={startStyle()}>
                        {asDate(startDate)}
                    </span>
                    {" - "}
                    <span style={endStyle()}>
                        {asDate(endDate)}
                    </span>
                    <span style={{ marginLeft: 10 }}><Weekdays selectedWeekdaysInt={discount.Weekdays} /></span>
                </div>
                </Item.Meta>
                <Item.Description>
                    <Item.Description>{getDiscountDescription(discount)}</Item.Description>
                    <Item.Description>{getDiscountSecondaryDescription(discount)}</Item.Description>
                </Item.Description>
            </div>
            {!inherited && (
                <Item.Extra>
                    <Button floated="right" icon="pencil" basic onClick={handleEdit} />
                    <Button floated="right" icon="trash" basic onClick={handleDelete} disabled={!discount.IsActive}/>
                </Item.Extra>
            )}
        </Item>
    );
}

RateDiscountItem.propTypes = {
    rateId: PropTypes.number.isRequired,
    currencyId: PropTypes.number,
    discount: PropTypes.shape({
        Id: PropTypes.number.isRequired,
        RateId: PropTypes.number.isRequired,
        RateDiscountTypeId: PropTypes.number.isRequired,
        RepeatDailyDiscount: PropTypes.bool,
		IsActive: PropTypes.bool,
        StartDate: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
            PropTypes.instanceOf(Date),
        ]).isRequired,
        EndDate: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
            PropTypes.instanceOf(Date),
        ]).isRequired,
        Weekdays: PropTypes.number,
        DiscountPercent: PropTypes.number,
        NumberOfNights: PropTypes.string,
        MinimumNumberOfNights: PropTypes.string,
        MaximumNumberOfNights: PropTypes.string,
        RateDiscountAmounts: PropTypes.array,
        ApplicableNight: PropTypes.number,
        DiscountApplicationTypeId: PropTypes.number
    }).isRequired,
    state: PropTypes.any,
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired
}
